import {
  Activity,
  ArrowLeft,
  FileText,
  Info,
  ListChecks,
  Map,
  SlidersHorizontal,
  Target,
} from "lucide-react";
import { NavLink, Outlet, useParams } from "react-router";

import { ButtonLink } from "@/components/button-link";
import { AsyncBoundary } from "@/components/data-state";
import { Badge } from "@/components/ui/badge";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import type { AdminChallenge } from "@/lib/api";
import { adminApi } from "@/lib/api";
import { cn } from "@/lib/utils";

import type { ChallengeContext } from "./use-challenge";

/**
 * One challenge, for the TA: everything about it, in the order it was created.
 *
 * Replaces an edit dialog (one long flat form) and a separate setup page (maze and worker,
 * repeating the dialog's maze step). The sections here are the create dialog's steps --
 * Basics, Maze, Scoring, Limits -- each a form that saves itself, so a TA fixing the
 * deadline is not shown the rubric, and one adjusting the rubric has the editor's full
 * width. Submissions and Sessions, the two views of what students did, sit under the same
 * nav rather than behind icons on a list row.
 */
export function AdminChallengeLayout() {
  const { id = "" } = useParams();
  const adminKey = useAdminKey();
  const challenge = useAsync(
    adminKey ? () => adminApi.challenge(id, adminKey) : null,
    [id, adminKey],
  );

  return (
    <div className="space-y-4">
      <ButtonLink variant="ghost" size="sm" to="/admin/challenges" className="-ml-2.5">
        <ArrowLeft className="size-4" aria-hidden />
        Challenges
      </ButtonLink>

      <AsyncBoundary state={challenge}>
        {(c) => (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-semibold tracking-tight">{c.name}</h1>
                  <Badge variant={c.type === "competition" ? "default" : "secondary"}>
                    {c.type === "competition" ? "Graded" : "Practice"}
                  </Badge>
                  <Badge variant="outline">
                    {c.simulator === "server" ? "on our server" : "on the student's machine"}
                  </Badge>
                  {!c.active ? <Badge variant="outline">Not accepting runs</Badge> : null}
                </div>
                <p className="text-muted-foreground mt-1 font-mono text-xs">{c.id}</p>
              </div>
              <ButtonLink variant="outline" size="sm" to={`/challenges/${c.id}`}>
                Student view
              </ButtonLink>
            </div>

            <div className="grid gap-6 md:grid-cols-[11rem_minmax(0,1fr)]">
              <SectionNav challenge={c} />
              <div className="min-w-0">
                <Outlet context={{ challenge: c, refetch: challenge.refetch } satisfies ChallengeContext} />
              </div>
            </div>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}

const SECTIONS = [
  { to: "", label: "Basics", icon: Info, end: true },
  { to: "maze", label: "Maze", icon: Map },
  { to: "scoring", label: "Scoring", icon: Target },
  { to: "limits", label: "Limits", icon: SlidersHorizontal },
  { to: "report", label: "Report", icon: FileText },
  { to: "submissions", label: "Submissions", icon: ListChecks, only: "client" },
  { to: "attempts", label: "Attempts", icon: Activity, only: "server" },
] as const;

function SectionNav({ challenge }: { challenge: AdminChallenge }) {
  return (
    <nav
      aria-label="Challenge sections"
      className="-mx-4 flex gap-1 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:flex-col md:px-0 [&::-webkit-scrollbar]:hidden"
    >
      {SECTIONS.filter((s) => !("only" in s) || s.only === challenge.simulator).map(
        (section) => {
          const Icon = section.icon;
          return (
            <NavLink
              key={section.to}
              to={section.to}
              end={"end" in section && section.end}
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              <Icon className="size-4 opacity-80" aria-hidden />
              {section.label}
            </NavLink>
          );
        },
      )}
    </nav>
  );
}

